import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Lock, ArrowRight } from "lucide-react";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user !== false) {
    return <Navigate to="/admin" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      nav("/admin");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Login failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-white flex items-center justify-center p-6 relative overflow-hidden" data-testid="admin-login-page">
      <div className="absolute inset-0 hero-glow pointer-events-none" />
      <div className="relative w-full max-w-md">
        <div className="flex items-center gap-3 mb-10">
          <div className="w-12 h-12 bg-orange-600 flex items-center justify-center">
            <Lock className="w-5 h-5" />
          </div>
          <div>
            <div className="font-display uppercase text-xs tracking-widest text-orange-500">MER Home Improvement</div>
            <div className="font-display uppercase text-2xl font-black">Admin Access</div>
          </div>
        </div>

        <form onSubmit={submit} className="bg-neutral-900 border border-neutral-800 p-8" data-testid="admin-login-form">
          <div className="mb-5">
            <label className="block text-xs uppercase tracking-widest text-neutral-400 mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-neutral-950 border border-neutral-800 focus:border-orange-600 outline-none px-4 py-3 text-white"
              placeholder="admin@merhomeimprovement.com"
              data-testid="admin-email-input"
              required
              autoComplete="email"
            />
          </div>
          <div className="mb-5">
            <label className="block text-xs uppercase tracking-widest text-neutral-400 mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-neutral-950 border border-neutral-800 focus:border-orange-600 outline-none px-4 py-3 text-white"
              placeholder="••••••••"
              data-testid="admin-password-input"
              required
              autoComplete="current-password"
            />
          </div>
          {error && (
            <div className="mb-4 text-sm text-red-400 border border-red-900/50 bg-red-950/50 px-3 py-2" data-testid="admin-login-error">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-3 bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white font-display font-bold uppercase tracking-wider py-3.5 transition-colors"
            data-testid="admin-login-submit"
          >
            {loading ? "Signing in…" : (<>Sign in <ArrowRight className="w-4 h-4" /></>)}
          </button>
        </form>

        <a href="/" className="block mt-6 text-center text-sm text-neutral-500 hover:text-orange-500" data-testid="back-to-site-link">
          ← Back to site
        </a>
      </div>
    </div>
  );
}
